/**
 * Component for handling email registration through a multi-step process
 */
import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';
import HealthDataDisclosure from './HealthDataDisclosure';
import HealthDataSummary from './HealthDataSummary';

/**
 * Multi-step registration process component for email-based registration
 * @param {Object} props - Component properties
 * @param {string} props.email - User's email address
 * @param {number} props.activeStep - Current active step in registration flow
 * @param {string} props.participantId - Participant's unique identifier
 * @param {boolean} props.loading - Loading state flag
 * @param {Function} props.onEmailChange - Handler for email value changes
 * @param {Function} props.onStoreEmail - Handler to save email to backend
 * @param {Function} props.onInitiateFitbitAuth - Handler to start Fitbit OAuth process
 * @param {Function} props.onRenewRegistration - Handler to restart registration
 * @param {Function} props.onVerifyEmail - Handler to verify email with code
 * @param {Function} props.runCronJobOnDate - Function to run a cron job for a direct registration download yersterday's data
 * @returns {JSX.Element} Rendered component
 * @description This component guides users through the email registration process, including email input, verification,
 * Fitbit account connection, and final registration steps. It uses Material-UI components for layout and styling.
 * It also includes validation for email format and handles loading states during asynchronous operations.
 * The component is designed to be reusable and modular, allowing for easy integration into larger applications.
 */
export default function EmailRegistrationDirectStepper({
  email,
  activeStep,
  participantId,
  loading,
  onEmailChange,
  onStoreEmail,
  onInitiateFitbitAuth,
  onRenewRegistration,
  onVerifyEmail,
  runCronJobOnDate
}) {
  // Validation state for email format
  const [isEmailValid, setIsEmailValid] = useState(false);

  // Verification code entered by user (for email verification step)
  const [verificationCode, setVerificationCode] = useState('');

  // Registration steps
  const steps = [
    'Enter Email Address',
    'Verify Email',
    'Connect Google Health',
    'Complete Registration'
  ];

  // Numeric, sanitized step index to avoid string/number mismatch in switch logic
  const stepIndex = Number(activeStep);

  /**
   * Validates email format and updates validation state
   * @param {string} email - Email address to validate
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email));
  };

  /**
   * Handler for email input changes
   * @param {Event} event - Input change event
   */
  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    onEmailChange(newEmail);
    validateEmail(newEmail);
  };

  /**
   * Handler for verification code input changes
   * @param {Event} event - Input change event
   */
  const handleVerificationCodeChange = (event) => {
    setVerificationCode(event.target.value);
  };

  /**
   * Handler for submitting verification code
   * Ensures verification result is handled and errors are logged
   */
  const handleVerifyEmail = () => {
    // Submit verification code to backend
    console.log("Verify with code:", verificationCode);
    onVerifyEmail(verificationCode)
      .then((result) => {
        console.log("Verification result:", result);
      })
      .catch((error) => {
        console.error("Error verifying email:", error);
      });

  };

  /**
   * Handler to trigger a backfill download for yesterday's data
   * Computes yesterday in YYYY-MM-DD and calls the provided API
   */
  const handleDownloadData = async () => {
    // Yesterday in the participant's own time zone (toISOString would shift it to UTC).
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    try {
      const ok = await runCronJobOnDate(formattedDate);
      if (ok) {
        console.log("Data download initiated successfully.");
      }
      // If ok is false, the context already surfaced an error via alerts; avoid duplicate console noise
    } catch (e) {
      // Defensive: if the function ever throws, log once
      console.error("Failed to initiate data download:", e?.message || e);
    }
  };

  /**
   * Renders content for the current step
   * Uses the sanitized numeric stepIndex to avoid type issues
   * @returns {JSX.Element|string} Current step content
   */
  const getStepContent = () => {
    switch (stepIndex) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              error={email !== '' && !isEmailValid}
              helperText={email !== '' && !isEmailValid ? "Please enter a valid email address" : ""}
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={onStoreEmail}
              disabled={!isEmailValid || loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Continue"}
            </Button>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              A verification code has been sent to your email address.
              Please enter the code below to continue.
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              variant="outlined"
              value={verificationCode}
              onChange={handleVerificationCodeChange}
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerifyEmail}
              disabled={verificationCode.length < 4 || loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Verify Email"}
            </Button>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Please connect your Google Health account to continue. When Google asks which data to share, tick Select all.
            </Typography>
            <HealthDataDisclosure />
            <Button
              variant="contained"
              color="primary"
              onClick={onInitiateFitbitAuth}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Connect Google Health"}
            </Button>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2"  align="center">
              Thank you for registering! Your participant ID is:
            </Typography>
            <Typography variant="h6" align="center" sx={{ mt: 2, mb: 2 }}>
              {participantId}
            </Typography>
            <Typography variant="body2" align="center">
              Click Next to prepare your complete yesterday&apos;s activity data.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadData}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Next"}
            </Button>
            <HealthDataSummary />
          </Box>
        );
      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              Thank you!
            </Typography>
            <Typography variant="body1" gutterBottom>
              You will receive an email with the complete data of yesterday&apos;s activity.
            </Typography>
          </Box>
        );
      case 5:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Your registration is complete!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={onRenewRegistration}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Renew Registration"}
            </Button>
          </Box>
        );
      default:
        return "Unknown step: " + stepIndex;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Use numeric stepIndex to keep the stepper in sync */}
      <Stepper activeStep={stepIndex} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ mt: 3 }}>
        {getStepContent()}
      </Box>
    </Box>
  );
}

EmailRegistrationDirectStepper.propTypes = {
  email: PropTypes.string.isRequired,
  activeStep: PropTypes.number.isRequired,
  participantId: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onStoreEmail: PropTypes.func.isRequired,
  onInitiateFitbitAuth: PropTypes.func.isRequired,
  onRenewRegistration: PropTypes.func.isRequired,
  // Function for verifying email with verification code
  onVerifyEmail: PropTypes.func.isRequired,
  runCronJobOnDate: PropTypes.func.isRequired
};
